import { useAuth } from "@clerk/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "wouter";
import type { SlotVersion, SlotKind, SignOff, OrgUser } from "scantling-common";
import {
  slotKindToTooltip,
  getContextualActionsForItem,
} from "scantling-common";
import {
  useApi,
  useListItemVersions,
  useListActions,
  useListUsers,
  useListSignOffs,
  useListPosts,
  useInvalidateLists,
  DateDisplay,
  PostSlug,
  PhaseChip,
  IntegrationSlug,
} from "./components.js";
import { HelmLayout } from "./layouts.js";
import { ActionButton } from "./action_button.js";
import { Actor } from "./actor.js";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DotsSixVertical,
  DotsThreeVertical,
  Info,
  Trash,
  Eraser,
} from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SlotEditor } from "./slot_editor.js";
import { NiceNameEditor } from "./nice_name_editor.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateActions,
} from "@/components/ui/empty-state";
import { StackSimple } from "@phosphor-icons/react";
import { ContextualActionsWidget } from "./contextual_actions_widget.js";

const SLOT_KINDS = ["text", "oneline", "micropost", "image"] as const;

interface SortableSlotCardProps {
  slot: SlotVersion;
  itemSlug: string;
  onSlotSave?: () => void;
  onSlotAction?: (verb: string, slotName: string) => void;
  onSlotKindChange?: (slotName: string, kind: string) => void;
  onSlotRename?: (oldName: string, newName: string) => void;
}

function SortableSlotCard({
  slot,
  itemSlug,
  onSlotSave,
  onSlotAction,
  onSlotKindChange,
  onSlotRename,
}: SortableSlotCardProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(slot.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slot.name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleNameClick = () => {
    setEditedName(slot.name);
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (editedName.trim() !== "" && editedName !== slot.name) {
      onSlotRename?.(slot.name, editedName.trim());
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setEditedName(slot.name);
      setIsEditingName(false);
    }
  };

  return (
    <Card ref={setNodeRef} style={style}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
            >
              <DotsSixVertical size={16} />
            </button>
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={e => setEditedName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className="border-b border-border bg-transparent px-0 py-0 text-base font-semibold outline-none"
              />
            ) : (
              <span
                onClick={handleNameClick}
                className="cursor-pointer hover:text-muted-foreground"
              >
                {slot.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={slot.kind}
              onValueChange={kind => onSlotKindChange?.(slot.name, kind)}
            >
              <SelectTrigger
                size="sm"
                className="h-6 w-auto gap-1 px-2 text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLOT_KINDS.map(kind => (
                  <SelectItem key={kind} value={kind}>
                    {kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info size={14} className="cursor-help text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                {slotKindToTooltip(slot.kind as SlotKind)}
              </TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <DotsThreeVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => onSlotAction?.("clearSlot", slot.name)}
                >
                  <Eraser size={14} className="mr-2" />
                  Clear content
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onSlotAction?.("deleteSlot", slot.name)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash size={14} className="mr-2" />
                  Delete slot
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <SlotEditor slot={slot} itemSlug={itemSlug} onSave={onSlotSave} />
      </CardContent>
    </Card>
  );
}

function statusBadgeVariant(
  status: SignOff["status"],
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "pending":
      return "secondary";
    case "signed":
      return "default";
    case "rejected":
      return "destructive";
    case "cancelled":
    case "revoked":
      return "outline";
  }
}

interface SignoffRowProps {
  signoff: SignOff;
  currentUserId: string | null | undefined;
  onSuccess: () => void;
  onReject: (signOffId: number, reason: string) => void;
  onCancel: (signOffId: number) => void;
}

function SignoffRow({
  signoff,
  currentUserId,
  onSuccess,
  onReject,
  onCancel,
}: SignoffRowProps) {
  const isAssignee =
    signoff.assignedUserId === null || signoff.assignedUserId === currentUserId;
  const isRequester =
    signoff.requestedActor.type === "user" &&
    signoff.requestedActor.userId === currentUserId;

  return (
    <div className="flex items-center justify-between gap-2 border-b py-2 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-1 items-center gap-2">
        <span className="text-sm font-medium">{signoff.kind}</span>
        <Badge variant={statusBadgeVariant(signoff.status)}>
          {signoff.status}
        </Badge>
        <span className="text-xs text-muted-foreground">
          v{signoff.itemVersion}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {signoff.status === "pending" && isAssignee && (
          <ActionButton
            verb="signOff"
            params={{ signOffId: signoff.id }}
            onActionSuccess={onSuccess}
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
          >
            Sign
          </ActionButton>
        )}
        {signoff.status === "signed" && (
          <ActionButton
            verb="revokeSignOff"
            params={{ signOffId: signoff.id }}
            onActionSuccess={onSuccess}
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
          >
            Revoke
          </ActionButton>
        )}
        {signoff.status === "pending" && (isAssignee || isRequester) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <DotsThreeVertical size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAssignee && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => {
                    const reason = window.prompt("Reason for rejection:");
                    if (!reason) return;
                    onReject(signoff.id, reason);
                  }}
                >
                  Reject
                </DropdownMenuItem>
              )}
              {isRequester && (
                <DropdownMenuItem onClick={() => onCancel(signoff.id)}>
                  Cancel request
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

interface SignoffsCardProps {
  itemSlug: string;
  signoffs: SignOff[];
  users: Map<string, OrgUser>;
  currentUserId: string | null | undefined;
  onSuccess: () => void;
}

function SignoffsCard({
  itemSlug,
  signoffs,
  users,
  currentUserId,
  onSuccess,
}: SignoffsCardProps) {
  const [kind, setKind] = useState("");
  const [assigneeId, setAssigneeId] = useState<string>("__anyone__");
  const pool = useApi();
  const { getToken } = useAuth();

  const runAction = useCallback(
    async (verb: string, params: Record<string, unknown>) => {
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({ verb, params });
        onSuccess();
      } finally {
        pool.release();
      }
    },
    [pool, getToken, onSuccess],
  );

  const handleReject = useCallback(
    (signOffId: number, reason: string) =>
      runAction("rejectSignOff", { signOffId, reason }),
    [runAction],
  );
  const handleCancel = useCallback(
    (signOffId: number) => runAction("cancelSignOffRequest", { signOffId }),
    [runAction],
  );

  const handleRequestSuccess = useCallback(() => {
    setKind("");
    setAssigneeId("__anyone__");
    onSuccess();
  }, [onSuccess]);

  const userList = [...users.values()];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign-offs</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {signoffs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No sign-offs yet.</p>
        ) : (
          <div className="flex flex-col">
            {signoffs.map(s => (
              <SignoffRow
                key={s.id}
                signoff={s}
                currentUserId={currentUserId}
                onSuccess={onSuccess}
                onReject={handleReject}
                onCancel={handleCancel}
              />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 border-t pt-3">
          <Input
            placeholder="Kind (e.g. editorial)"
            value={kind}
            onChange={e => setKind(e.target.value)}
          />
          <Select value={assigneeId} onValueChange={setAssigneeId}>
            <SelectTrigger>
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__anyone__">Anyone</SelectItem>
              {userList.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.firstName && u.lastName
                    ? `${u.firstName} ${u.lastName}`
                    : (u.email ?? u.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ActionButton
            verb="requestSignOff"
            params={{
              itemSlug,
              kind,
              assignedUserId: assigneeId === "__anyone__" ? null : assigneeId,
            }}
            disabled={!kind.trim()}
            onActionSuccess={handleRequestSuccess}
            size="sm"
          >
            Request sign-off
          </ActionButton>
        </div>
      </CardContent>
    </Card>
  );
}

function ItemPageContent() {
  const { itemSlug } = useParams<{ itemSlug: string }>();
  const { getToken, userId } = useAuth();
  const pool = useApi();
  const invalidate = useInvalidateLists();

  const versionsResult = useListItemVersions(`slug:${itemSlug}`);
  const versions = versionsResult.data?.entries ?? [];
  const actionsResult = useListActions(
    `verb:createItem success:true dataSlug:${itemSlug}`,
  );
  const usersResult = useListUsers();
  const signoffsResult = useListSignOffs(`itemSlug:${itemSlug}`);
  const postsResult = useListPosts(`itemSlug:${itemSlug}`);
  const posts = postsResult.data?.entries ?? [];

  const loading = versionsResult.isLoading;
  const error = versionsResult.error ?? null;

  const users = useMemo(
    () => new Map((usersResult.data?.entries ?? []).map(u => [u.id, u])),
    [usersResult.data],
  );
  const creator = actionsResult.data?.entries[0]?.actor ?? null;
  const signoffs = signoffsResult.data?.entries ?? [];

  const latestFromVersions = useMemo(
    () =>
      versions.length > 0
        ? versions.reduce((a, b) => (a.version > b.version ? a : b))
        : null,
    [versions],
  );
  const [slots, setSlots] = useState<SlotVersion[]>([]);
  useEffect(() => {
    if (latestFromVersions) setSlots(latestFromVersions.slots);
  }, [latestFromVersions]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchItem = useCallback(() => {
    invalidate("listItemVersions");
  }, [invalidate]);

  const fetchSignoffs = useCallback(() => {
    invalidate("listSignOffs");
  }, [invalidate]);

  const handleAddSlotSuccess = useCallback(() => {
    fetchItem();
  }, [fetchItem]);

  const handleSignoffSuccess = useCallback(() => {
    fetchSignoffs();
    fetchItem();
  }, [fetchSignoffs, fetchItem]);

  const handleNiceNameChange = useCallback(
    async (niceName: string | null) => {
      if (!itemSlug) return;
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "updateItemNiceName",
          params: { itemSlug, niceName },
        });
        fetchItem();
      } finally {
        pool.release();
      }
    },
    [pool, getToken, itemSlug, fetchItem],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = slots.findIndex(s => s.name === active.id);
      const newIndex = slots.findIndex(s => s.name === over.id);
      const newSlots = arrayMove(slots, oldIndex, newIndex);
      setSlots(newSlots);

      // Call API to persist order
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "reorderSlots",
          params: { itemSlug, slotNames: newSlots.map(s => s.name) },
        });
      } finally {
        pool.release();
      }
    },
    [slots, pool, getToken, itemSlug],
  );

  const handleSlotAction = useCallback(
    async (verb: string, slotName: string) => {
      if (!itemSlug) return;
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb,
          params: { itemSlug, slotName },
        });
        fetchItem();
      } finally {
        pool.release();
      }
    },
    [pool, getToken, itemSlug, fetchItem],
  );

  const handleSlotKindChange = useCallback(
    async (slotName: string, kind: string) => {
      if (!itemSlug) return;
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "updateSlotKind",
          params: { itemSlug, slotName, kind },
        });
        fetchItem();
      } finally {
        pool.release();
      }
    },
    [pool, getToken, itemSlug, fetchItem],
  );

  const handleSlotRename = useCallback(
    async (oldName: string, newName: string) => {
      if (!itemSlug) return;
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "renameSlot",
          params: { itemSlug, oldName, newName },
        });
        fetchItem();
      } finally {
        pool.release();
      }
    },
    [pool, getToken, itemSlug, fetchItem],
  );

  const handleItemActionSuccess = useCallback(() => {
    fetchItem();
  }, [fetchItem]);

  const latestVersion =
    versions.length > 0
      ? versions.reduce((a, b) => (a.version > b.version ? a : b))
      : null;

  const nextSlotName = useMemo(() => {
    return `slot-${slots.length + 1}`;
  }, [slots]);

  if (loading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (error) {
    return <div className="text-destructive">{error}</div>;
  }

  if (!latestVersion) {
    return <div className="text-muted-foreground">Item not found.</div>;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2 flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <NiceNameEditor
                niceName={latestVersion.niceName}
                slug={itemSlug!}
                onSave={handleNiceNameChange}
              />
              {(() => {
                const actions = getContextualActionsForItem(latestVersion);
                return (
                  <ContextualActionsWidget
                    actions={actions}
                    onActionSuccess={handleItemActionSuccess}
                  />
                );
              })()}
            </CardTitle>
          </CardHeader>
        </Card>
        {slots.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState>
                <EmptyStateIcon>
                  <StackSimple />
                </EmptyStateIcon>
                <EmptyStateTitle>No slots yet</EmptyStateTitle>
                <EmptyStateDescription>
                  Add a slot to start building your content.
                </EmptyStateDescription>
                <EmptyStateActions>
                  <ActionButton
                    verb="addSlot"
                    params={{ itemSlug, name: nextSlotName, kind: "text" }}
                    onActionSuccess={handleAddSlotSuccess}
                  >
                    Add Slot
                  </ActionButton>
                </EmptyStateActions>
              </EmptyState>
            </CardContent>
          </Card>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={slots.map(s => s.name)}
                strategy={verticalListSortingStrategy}
              >
                {slots.map(slot => (
                  <SortableSlotCard
                    key={slot.name}
                    slot={slot}
                    itemSlug={itemSlug!}
                    onSlotSave={fetchItem}
                    onSlotAction={handleSlotAction}
                    onSlotKindChange={handleSlotKindChange}
                    onSlotRename={handleSlotRename}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <ActionButton
              verb="addSlot"
              params={{ itemSlug, name: nextSlotName, kind: "text" }}
              onActionSuccess={handleAddSlotSuccess}
              variant="outline"
            >
              Add Slot
            </ActionButton>
          </>
        )}
      </div>
      <div className="col-span-1 flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="flex flex-col gap-3 text-xs">
              {latestVersion.sourceSlug ? (
                <div className="flex flex-col gap-1">
                  <dt className="text-muted-foreground">Source</dt>
                  <dd className="text-sm">
                    <IntegrationSlug slug={latestVersion.sourceSlug} />
                  </dd>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <dt className="text-muted-foreground">Creator</dt>
                  <dd>
                    <Actor actor={creator} users={users} />
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
        <SignoffsCard
          itemSlug={itemSlug!}
          signoffs={signoffs}
          users={users}
          currentUserId={userId}
          onSuccess={handleSignoffSuccess}
        />
        <Card>
          <CardHeader>
            <CardTitle>Posts</CardTitle>
          </CardHeader>
          <CardContent>
            {posts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No posts yet.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {posts
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime(),
                  )
                  .map(post => (
                    <li
                      key={post.slug}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <PostSlug slug={post.slug} post={post} />
                        <span className="text-muted-foreground">
                          {post.channelSlug}
                        </span>
                      </div>
                      <PhaseChip phase={post.phase} />
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Versions</CardTitle>
          </CardHeader>
          <CardContent>
            {versions.length === 0 ? (
              <p className="text-xs text-muted-foreground">No versions.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {versions
                  .sort((a, b) => b.version - a.version)
                  .map(v => (
                    <li
                      key={v.version}
                      className="flex justify-between gap-2 text-xs"
                    >
                      <span className="font-mono">v{v.version}</span>
                      <DateDisplay
                        value={v.createdAt}
                        className="text-muted-foreground"
                      />
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const ItemPage = () => {
  return (
    <HelmLayout>
      <ItemPageContent />
    </HelmLayout>
  );
};
